import { AsyncPipe, NgClass } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatInput } from '@angular/material/input';
import { debounceTime, map, mergeMap, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Project } from '../../models/project.module';
import { User } from '../../models/user.model';
import { NewReportService } from '../../services/newReport.service';
import { ProjectService } from '../../services/project.service';
import { ToastNotificationService } from '../../services/toast-notification.service';
import { UserService } from '../../services/user.service';
import { UserLogoComponent } from '../user-logo/user-logo.component';
import { MatSelect } from '@angular/material/select';
import { HttpErrorResponse } from '@angular/common/http';
import { findFirstNonEmptyString } from '../../utils';
import { SharingService } from '../../services/sharing.service';
import { InlineSvgComponent } from '../inline-svg/inline-svg.component';

@Component({
  selector: 'frmg-share-modal',
  templateUrl: './share-modal.component.html',
  styleUrls: ['./share-modal.component.scss'],
  imports: [
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatButton,
    UserLogoComponent,
    AsyncPipe,
    MatSelect,
    NgClass,
    InlineSvgComponent
  ]
})
export class ShareModalComponent<T> implements OnInit {
  public userSearchCtrl: FormControl;
  public roleType: FormControl;
  public roleTypes: FormGroup;
  public entityName: string;
  public users$: Observable<Array<User>>;
  public sharedUsers: User[] = [];
  public currentUser: User;
  public type: string;
  private updated: boolean = false;
  private initialCtrlsState = {};

  constructor(
    private dialogRef: MatDialogRef<ShareModalComponent<T>>,
    @Inject(MAT_DIALOG_DATA) public data: { entity: any, canShare: boolean },
    private fb: FormBuilder,
    private userService: UserService,
    private toastNotificationService: ToastNotificationService,
    private projectService: ProjectService,
    private reportService: NewReportService,
    private sharingService: SharingService
  ) {
    this.roleType = this.fb.control('read');
    this.roleTypes = this.fb.group({});
    this.userSearchCtrl = this.fb.control('', [this.userValidator(), Validators.required]);
    this.currentUser = JSON.parse(localStorage.getItem('pomeriumAuth')) as User;

    // @ts-ignore
    this.entityName = this.data.entity.name;
    this.type = this.data.entity.path ? 'project' : 'report';
    this.users$ = this.userSearchCtrl.valueChanges.pipe(
      debounceTime(500),
      mergeMap((query) =>
        query
          ? this.userService.searchTeamUsers(query).pipe(
            map((users) => {
              return users.filter(
                (user) =>
                  !this.sharedUsers.find((el) => el.email === user.email)
              );
            }),
            catchError((_err) => of([]))
          )
          : of([])
      )
    );

    this.sharedUsers.push(this.data.entity.user);
    if (this.data.entity.shares) {
      this.data.entity.shares.forEach((share) => {
        // this.userService.getUserById(id).subscribe((user) => {
        const prermissionType = share.permissions.length === 1 ? 'read' : 'all';
        this.roleTypes.addControl(share.shared_with_user.email, this.fb.control(prermissionType));
        this.sharedUsers.push(share.shared_with_user);
        // });
      });
    }

    this.roleTypes.valueChanges.subscribe((value) => {
      for (const controlName in value) {
        if (value[controlName] !== this.initialCtrlsState[controlName]) {
          const share = this.data.entity.shares.find((share) => (share.shared_with_user?.email === controlName || share.email === controlName));
          if (share) {
            if (value[controlName] !== 'revoke') {
              this.sharingService.updateSharePermission(share.id, value[controlName] === 'all' ? ['READ', 'CREATE', 'UPDATE', 'DELETE'] : ['READ']).subscribe(_ => {
              }, error => {
                this.toastNotificationService.showNotification({
                  type: 'error',
                  message: findFirstNonEmptyString(error.error.detail)
                });
              });
            } else {
              this.sharingService.deleteShare(share.id).subscribe(_ => {
                const index = this.sharedUsers.findIndex(user => user.email === controlName);
                const dataIndex = this.data.entity.shares.findIndex(user => user.email === controlName || share.shared_with_user?.email === controlName);
                this.sharedUsers.splice(index, 1);
                this.data.entity.shares.splice(dataIndex, 1);
                this.updated = true;
              }, error => {
                this.toastNotificationService.showNotification({
                  type: 'error',
                  message: findFirstNonEmptyString(error.error.detail)
                });
              });
            }}
          this.updated = true;
        }
      }
      this.initialCtrlsState = { ...value };
    });
  }

  ngOnInit(): void {
  }

  public getUserRoleCtrl(email: string): FormControl {
    return this.roleTypes.controls[email] as FormControl;
  }

  public closeDialog(_result?: any): void {
    this.dialogRef.close(this.updated);
  }

  displayName(option: User): string {
    return option ? option.email : '';
  }

  public userValidator(): ValidatorFn {
    return (ctrl: FormControl): ValidationErrors | null => {
      const user = ctrl.value;
      return user.id ? null : { userInvalid: true };
    };
  }

  public share(): void {
    const user = this.userSearchCtrl.value;
    let request;
    if (this.type === 'project') {
      request = this.projectService.shareProject(
        {
          shared_with_user_id: user.id,
          project_id: this.data.entity.id,
          permissions: this.roleType.value === 'all' ? ['READ', 'CREATE', 'UPDATE', 'DELETE'] : ['READ']
        }
      );
    } else {
      request = this.reportService.shareReport(
        { user_id: user.user_id },
        (this.data.entity as Project).id
      );
    }
    request.subscribe(
      (resp) => {
        this.userService.getUserById(user.id).subscribe((user) => {
          const prermissionType = resp.permissions.length === 1 ? 'read' : 'all';
          this.roleTypes.addControl(user.email, this.fb.control(prermissionType));
          this.sharedUsers.push({ ...user, ...resp } as User);
          this.data.entity.shares.push({ ...user, ...resp } as User);
          this.userSearchCtrl.setValue(null, { emitEvent: true });
          this.updated = true;
        });
      },
      (error: HttpErrorResponse) => {
        this.toastNotificationService.showNotification({
          type: 'error',
          message: findFirstNonEmptyString(error.error.detail[0].msg)
        });
      }
    );
  }

  public revoke(index: number, user: User): void {
    let request;
    if (this.type === 'project') {
      request = this.projectService.removeShareProject(
        { user_id: user.user_id || user.id },
        (this.data.entity as Project).id
      );
    } else {
      request = this.reportService.removeShareReport();
    }
    request.subscribe(
      () => {
        this.sharedUsers.splice(index, 1);
        this.updated = true;
      },
      (error: HttpErrorResponse) => {
        this.toastNotificationService.showNotification({
          type: 'error',
          message: findFirstNonEmptyString(error.error.detail[0].msg)
        });
      }
    );
  }
}
